import { useMemo, useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import moment from 'moment'
import { Marker } from '@react-google-maps/api'
import { Typography } from '@mui/material'

import {
	tracksSvgPath,
	MARKER_TRACK_SCALE_ACTIVE,
	MARKER_TRACK_SCALE_NORMAL,
	TRACK_ANCHOR_X,
	TRACK_ANCHOR_Y,
} from '../../../../../data/map/mapHelpers'
import {
	formatSpeedToKilometersPerHour,
	formatSpeed,
} from '../../../../../../utils/functions/mapUtils'
import { rotateImageAsync } from '../helper/functions'
import arrowIcon from '../../../../../../static/img/arrow-up.png'
import stopIcon from '../../../../../../static/img/stop.png'

const svgIcons = {
	currentLocation: {
		path: 'M215.7 499.2C267 435 384 279.4 384 192C384 86 298 0 192 0S0 86 0 192c0 87.4 117 243 168.3 307.2c12.3 15.3 35.1 15.3 47.4 0zM192 128a64 64 0 1 1 0 128 64 64 0 1 1 0-128z',
		color: '#575fcf',
		borderColor: '#1e272e',
		scale: 0.06,
		pointX: 180,
		pointY: 600,
	},
	arrow: {
		url: arrowIcon,
		scaledSize: 28,
	},

	stop: {
		url: stopIcon,
		scaledSize: 20,
	},
}

export function DriverHeartBeatComponent(props) {
	const {
		data,
		index,
		handleMouseOutChangeMarkerInfo,
		handleMouseOverChangeMarkerInfo,
		isLastHeartBeat,
	} = props

	const { environment } = useSelector((state) => state)

	const [hover, setHover] = useState(false)

	const [rotatedArrowUrl, setRotatedArrowUrl] = useState(null)

	const heartBeatId = useMemo(() => {
		return `heartbeat-${data.key || index}`
	}, [data, index])

	const position = useMemo(
		() => ({
			lat: data.lat,
			lng: data.lon,
		}),
		[data?.lat, data?.lon]
	)

	useEffect(() => {
		if (isLastHeartBeat && data?.moving) {
			rotateImageAsync(svgIcons.arrow.url, data.bearing || 0)
				.then((result) => {
					setRotatedArrowUrl(result)
				})
				.catch(console.error)
		}
	}, [data?.bearing, isLastHeartBeat, data?.moving])

	const markerIcon = useMemo(() => {
		if (isLastHeartBeat) {
			let icon = {}
			const isMoving = data?.moving

			if (isMoving && !rotatedArrowUrl) {
				return null
			}

			if (isMoving) {
				icon = { ...svgIcons?.arrow, url: rotatedArrowUrl }
			} else {
				icon = svgIcons.stop
			}

			return {
				url: icon?.url,
				scaledSize: new window.google.maps.Size(icon?.scaledSize, icon?.scaledSize),
				anchor: new window.google.maps.Point(15, 15),
			}
		}

		if (data.bearing) {
			if (data.bearing === -1) {
				return {
					path: window.google.maps.SymbolPath.CIRCLE,
					scale: 6,
					fillColor: '#136792',
					fillOpacity: 1,
					strokeColor: '#ffffff',
					strokeWeight: 1,
				}
			}

			return {
				fillColor: '#136792',
				fillOpacity: 1,
				path: tracksSvgPath,
				scale: hover ? MARKER_TRACK_SCALE_ACTIVE : MARKER_TRACK_SCALE_NORMAL,
				strokeColor: '#ffffff',
				strokeWeight: 1,
				rotation: data.bearing,
				anchor: new window.google.maps.Point(TRACK_ANCHOR_X, TRACK_ANCHOR_Y),
			}
		}

		return {
			path: window.google.maps.SymbolPath.CIRCLE,
			scale: 6,
			fillColor: '#136792',
			fillOpacity: 1,
			strokeColor: '#ffffff',
			strokeWeight: 1,
		}
	}, [data, hover, isLastHeartBeat, rotatedArrowUrl])

	useEffect(() => {
		if (!hover) {
			handleMouseOutChangeMarkerInfo(heartBeatId)
			return
		}

		const isMetricUser = environment?.userCurrent?.units === 'metric'
		const speedValue = isMetricUser
			? formatSpeedToKilometersPerHour(data.speed)
			: formatSpeed(data.speed)

		const userOffset = environment?.userCurrent?.time?.timezone?.offset

		handleMouseOverChangeMarkerInfo(heartBeatId, () => {
			return {
				id: heartBeatId,
				content: (
					<div>
						<Typography variant="body1">
							{moment.utc(data.timestamp).utcOffset(userOffset).format('MM/DD/YY [at] hh:mm:ss A')}
						</Typography>
						{data.speed >= 0 && <Typography variant="body1">{speedValue}</Typography>}
					</div>
				),
			}
		})
	}, [hover, heartBeatId, data, environment, index])

	return (
		<Marker
			key={heartBeatId}
			position={position}
			icon={markerIcon}
			onMouseOver={() => setHover(true)}
			onMouseOut={() => setHover(false)}
		/>
	)
}
